import React from 'react';
import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet-async';

const ServiciosPage = () => {
  const services = [
    {
      id: 1,
      title: "Programa MUN",
      subtitle: "Formación Diplomática y Competencias Globales",
      description: "El Programa MUN de AV Global es una experiencia formativa diseñada para desarrollar en los estudiantes las habilidades intelectuales, comunicativas y humanísticas más relevantes del siglo XXI. A través de un entrenamiento estructurado y participativo, los alumnos aprenden a investigar, argumentar, debatir, negociar y liderar con una visión internacional.",
      subSections: [
        {
          title: "¿Qué desarrollan los estudiantes?",
          points: [
            "Oratoria persuasiva y comunicación estratégica",
            "Capacidad de negociación y pensamiento diplomático",
            "Investigación profunda, análisis comparado y redacción técnica",
            "Liderazgo estudiantil, trabajo en equipo y empatía intercultural",
            "Pensamiento crítico y resolución de conflictos",
            "Cultura de paz y responsabilidad global"
          ]
        },
        {
          title: "¿Por qué es un valor agregado para el colegio?",
          points: [
            "El MUN se convierte en una vitrina de excelencia institucional, fortaleciendo el perfil académico del colegio y posicionándolo como referente en liderazgo y ciudadanía global."
          ]
        }
      ],
      image: "mun.png" // MUN / Debate context
    },
    {
      id: 2,
      title: "Orientación Internacional",
      subtitle: "Admisiones, Becas y Estrategia Global",
      description: "El servicio de Orientación Internacional de AV Global acompaña a estudiantes y familias en todas las etapas del proceso de postulación universitaria en el extranjero. Nuestro modelo integra estrategia personalizada, gestión documental, preparación académica, asesoría para becas y construcción de un perfil competitivo en mercados educativos de alto nivel.",
      subSections: [
        {
          title: "Acompañamos en:",
          points: [
            "Mapeo de universidades y programas alineados al perfil",
            "Construcción y fortalecimiento del perfil académico",
            "Ensayos, cartas, recomendaciones y portafolios",
            "Preparación para entrevistas y asesoría en visas",
            "Postulación a becas académicas, deportivas o de mérito",
            "Planificación temprana y revisión continua"
          ]
        },
        {
          title: "Nuestra diferencia:",
          points: [
            "Estrategia clara, ordenada y viable que maximiza oportunidades.",
            "Acompañamiento cercano, técnico y transparente para las familias."
          ]
        }
      ],
      image: "orientacion.png" // University context
    },
    {
      id: 3,
      title: "Doble Titulación Escolar",
      subtitle: "High School Diploma Internacional",
      description: "AV Global implementa y gestiona programas de doble titulación en alianza con instituciones estadounidenses acreditadas. Este servicio permite que los estudiantes obtengan un High School Diploma reconocido internacionalmente mientras cursan sus estudios escolares en Perú, fortaleciendo su perfil académico y ampliando sus oportunidades universitarias en el mundo.",
      subSections: [
        {
          title: "¿Qué incluye nuestro servicio?",
          points: [
            "Homologación curricular y diseño de la ruta de aprendizaje dual",
            "Gestión académica y coordinación con la institución internacional",
            "Monitoreo del desempeño y comunicación con familias",
            "Capacitación y acompañamiento al equipo docente",
            "Implementación sin costos estructurales para la institución"
          ]
        },
        {
          title: "Beneficios Clave:",
          points: [
            "Estudiantes: Certificación oficial, acceso preferencial a universidades, inglés académico.",
            "Colegio: Potencia la propuesta educativa, aumenta competitividad e internacionalización."
          ]
        }
      ],
      image: "titulacion.png" // Diploma / Graduation
    },
    {
      id: 4,
      title: "Liderazgo Global y Redes",
      subtitle: "Conexiones que Transforman",
      description: "En AV Global facilitamos la participación de estudiantes y colegios en ecosistemas internacionales de liderazgo, innovación y movilidad académica. Conectamos a nuestras instituciones aliadas con redes educativas globales, espacios de mentoría especializada y programas de desarrollo estudiantil en el extranjero.",
      subSections: [
        {
          title: "¿Qué ofrecemos?",
          points: [
            "Mentorías personalizadas con profesionales internacionales",
            "Programas de liderazgo global para estudiantes y docentes",
            "Movilidad académica: viajes formativos, STEAM y conferencias",
            "Vinculación con organizaciones globales de debate e investigación"
          ]
        },
        {
          title: "Impacto:",
          points: [
            "Colegios: Amplía visibilidad internacional y conecta docentes.",
            "Estudiantes: Experiencias reales, identidad global y acceso a mentores."
          ]
        }
      ],
      image: "liderazgo.png" // Networking / Diverse group
    }
  ];

  return (
    <>
      <Helmet>
        <title>Servicios | AV Global</title>
        <meta></meta>
      </Helmet>
      <div className="bg-white font-sans text-gray-800">
        
        {/* 1. HERO HEADER */}
        <section className="relative pt-24 pb-16 px-6 md:px-12 lg:px-16 overflow-hidden">
          {/* Decorative Background Blur */}
          <div className="absolute top-[-20%] left-[-10%] w-[800px] h-[800px] bg-sky-100/50 blur-[120px] rounded-full pointer-events-none z-0"></div>

          <div className="max-w-7xl mx-auto relative z-10 text-center">
            <div className="inline-block px-6 py-1.5 border border-gray-400 rounded-full mb-6 bg-white/50 backdrop-blur-sm">
              <span className="text-gray-600 text-sm font-medium">Nuestros Servicios</span>
            </div>
            
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-[#1a3b5c] mb-6 leading-tight tracking-tight">
              Soluciones educativas de <span className="text-[#5B9BB0]">clase mundial.</span>
            </h1>
            
            <p className="text-lg md:text-xl text-gray-500 max-w-3xl mx-auto leading-relaxed">
              Diseñamos programas que conectan el talento local con oportunidades globales, elevando el estándar educativo de nuestras instituciones aliadas.
            </p>
          </div>
        </section>
        <div className='w-full border border-gray'></div>

        {/* 2. SERVICES CONTENT */}
        <section className="py-24 px-6 md:px-12 lg:px-16">
          <div className="max-w-[90rem] mx-auto flex flex-col gap-24 lg:gap-32">
            
            {services.map((service, index) => (
              <motion.div 
                id={index}
                key={service.id}
                initial={index === 0 ? { opacity: 1, y: 0 } : { opacity: 0, y: 50 }}
                whileInView={index === 0 ? undefined : { opacity: 1, y: 0 }}
                animate={index === 0 ? { opacity: 1, y: 0 } : undefined}
                viewport={index === 0 ? undefined : { once: true, amount: 0.3 }}
                transition={{ duration: 0.7, delay: index === 0 ? 0 : 0.1 }}
                className={`flex flex-col ${index % 2 === 0 ? 'lg:flex-row' : 'lg:flex-row-reverse'} gap-12 lg:gap-20 items-center`}
              >
                
                {/* Image Column (50%) */}
                <motion.div 
                  initial={index === 0 ? { opacity: 1, x: 0 } : { opacity: 0, x: index % 2 === 0 ? -50 : 50 }}
                  whileInView={index === 0 ? undefined : { opacity: 1, x: 0 }}
                  animate={index === 0 ? { opacity: 1, x: 0 } : undefined}
                  viewport={index === 0 ? undefined : { once: true, amount: 0.3 }}
                  transition={{ duration: 0.7, delay: index === 0 ? 0.1 : 0.2 }}
                  className="w-full lg:w-1/2"
                >
                  <div className="relative rounded-[2.5rem] overflow-hidden shadow-xl aspect-[4/3] group">
                    <div className="absolute inset-0 bg-[#1a3b5c]/10 group-hover:bg-transparent transition-colors duration-500 z-10"></div>
                    <img 
                      src={service.image} 
                      alt={service.title} 
                      className="w-full h-full object-cover transform transition-transform duration-700 group-hover:scale-105"
                    />
                    
                    {/* Decorative Number Badge */}
                    <div className="absolute top-6 left-6 bg-white/90 backdrop-blur-md px-4 py-2 rounded-full z-20 shadow-sm border border-white/50">
                      <span className="text-[#1a3b5c] font-bold text-lg">0{service.id}</span>
                    </div>
                  </div>
                </motion.div>

                {/* Content Column (50%) */}
                <motion.div 
                  initial={index === 0 ? { opacity: 1, x: 0 } : { opacity: 0, x: index % 2 === 0 ? 50 : -50 }}
                  whileInView={index === 0 ? undefined : { opacity: 1, x: 0 }}
                  animate={index === 0 ? { opacity: 1, x: 0 } : undefined}
                  viewport={index === 0 ? undefined : { once: true, amount: 0.3 }}
                  transition={{ duration: 0.7, delay: index === 0 ? 0.2 : 0.3 }}
                  className="w-full lg:w-1/2 flex flex-col justify-center"
                >
                  <h2 className="text-3xl md:text-4xl font-bold text-[#1a3b5c] mb-2">
                    {service.title}
                  </h2>
                  <h3 className="text-xl text-[#5B9BB0] font-medium mb-6">
                    {service.subtitle}
                  </h3>
                  
                  <p className="text-gray-600 text-lg leading-relaxed mb-8">
                    {service.description}
                  </p>

                  {/* Subsections / Lists */}
                  <div className="space-y-8">
                    {service.subSections.map((section, idx) => (
                      <div key={idx} className="bg-slate-50 rounded-[2rem] p-8 border border-slate-100">
                        <h4 className="font-bold text-[#254E70] mb-4 text-lg">
                          {section.title}
                        </h4>
                        <ul className="grid gap-3">
                          {section.points.map((point, pIdx) => (
                            <li key={pIdx} className="flex items-start gap-3 text-gray-600 text-sm md:text-base leading-snug">
                              <div className="mt-1.5 w-1.5 h-1.5 rounded-full bg-[#5B9BB0] flex-shrink-0"></div>
                              <span>{point}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    ))}
                  </div>

                </motion.div>

              </motion.div>
            ))}

          </div>
        </section>

        {/* 3. ENHANCED CTA FOOTER HOOK */}
        <motion.section 
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.7 }}
          className="relative py-24 px-6 md:px-12 mx-4 md:mx-8 mb-8 rounded-[3rem] overflow-hidden text-center text-white"
        >
          
          {/* Background Image with Overlay */}
          <div className="absolute inset-0 bg-[url('https://images.unsplash.com/photo-1523240795612-9a054b0db644?ixlib=rb-4.0.3&auto=format&fit=crop&w=2000&q=80')] bg-cover bg-center"></div>
          <div className="absolute inset-0 bg-[#1a3b5c]/90 backdrop-blur-sm"></div>

          <div className="relative z-10 max-w-4xl mx-auto flex flex-col items-center">
            
            {/* Decorative Icon Wrapper */}
            <motion.div 
              initial={{ opacity: 0, scale: 0.8 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.2 }}
              className="inline-block p-3 bg-white/10 rounded-2xl mb-8 backdrop-blur-md border border-white/20 text-white"
            >
              <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z"/>
              </svg>
            </motion.div>
            
            <motion.h2 
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.3 }}
              className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6 tracking-tight"
            >
              ¿Listo para transformar <br className="hidden md:block" /> la educación?
            </motion.h2>
            
            <motion.p 
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.4 }}
              className="text-blue-100 text-lg md:text-xl mb-10 leading-relaxed max-w-2xl font-light"
            >
              Agenda una reunión con nuestros especialistas y descubre cómo implementar estos programas en tu institución.
            </motion.p>
            
            <a href="https://calendly.com/avconsulting-info/30min?month=2026-03" target="_blank" rel="noopener noreferrer">
              <motion.button 
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: 0.5 }}
                whileHover={{ scale: 1.05, y: -4 }}
                whileTap={{ scale: 0.98 }}
                className="group bg-white text-[#1a3b5c] hover:bg-blue-50 px-10 py-5 rounded-full font-bold text-lg transition-all duration-300 shadow-xl hover:shadow-2xl hover:shadow-blue-900/20 flex items-center gap-3"
              >

                <span>Agendar Asesoría Gratuita</span>
                <svg className="w-5 h-5 group-hover:translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 8l4 4m0 0l-4 4m4-4H3"/>
                </svg>
              </motion.button>
            </a>
          </div>
        </motion.section>

      </div>
    </>
  );
};

export default ServiciosPage;